/**
 * tts-client — browser-side text-to-speech for English dictation.
 *
 * Source of truth: docs/spec/LLM_WIKI_REVIEW_SPEC.md §4 (`POST /tts`) + §7.2.
 *
 * Primary path: ask the Worker `POST {WORKER_URL}/tts` for audio bytes
 * (Cloudflare Workers AI melotts) and play them. If the Worker is
 * unavailable (non-2xx / network error / 501 not-configured), fall back to
 * the browser `window.speechSynthesis` (Web Speech API) with an en-US voice.
 *
 * `voice` lets callers choose which engine to prefer (some words sound off on
 * one engine but fine on the other, so the dictation UI offers both):
 *   - 'auto'   (default) — cloud first, device fallback (existing behaviour).
 *   - 'cloud'  — force the Worker `/tts` (natural Aura voice); device fallback.
 *   - 'device' — go straight to `window.speechSynthesis` (built-in en-US),
 *                without hitting the Worker; cloud fallback if no device voice.
 *
 * 學習/遊戲分離:純學習側 TTS,不觸碰任何戰鬥數值。
 */

/** Which TTS engine to prefer. */
export type TtsVoice = 'cloud' | 'device' | 'auto';

const WORKER_URL =
  (import.meta as any).env?.VITE_WORKER_URL ?? 'http://localhost:8787';

/** The currently-playing HTMLAudioElement (worker path), if any. */
let currentAudio: HTMLAudioElement | null = null;

/**
 * Speak `text`. Resolves once playback finishes (or fails silently to fallback).
 *
 * `speaker` (Aura-2 voice name) / `engine` ('melotts') only apply to the cloud
 * Worker path — they let the dictation UI pin which natural voice plays. The
 * device path ignores them (the OS picks the voice).
 */
export async function speak(
  text: string,
  opts?: {
    lang?: string;
    rate?: number;
    voice?: TtsVoice;
    speaker?: string;
    engine?: 'melotts';
  },
): Promise<void> {
  const lang = opts?.lang ?? 'en';
  const rate = opts?.rate ?? 1;
  const voice = opts?.voice ?? 'auto';
  const speaker = opts?.speaker;
  const engine = opts?.engine;

  // Stop anything already playing before starting a new utterance.
  stopSpeaking();

  // 'device' — use the built-in browser voice directly; only reach for the
  // cloud Worker if the device has no Web Speech support at all.
  if (voice === 'device') {
    const spoke = await speakViaSpeechSynthesis(text, lang, rate);
    if (!spoke) await speakViaWorker(text, lang, rate, speaker, engine);
    return;
  }

  // 'cloud' / 'auto' — try the Worker first, fall back to the device voice.
  const spoke = await speakViaWorker(text, lang, rate, speaker, engine);
  if (!spoke) await speakViaSpeechSynthesis(text, lang, rate);
}

/**
 * Try the Worker `/tts` path. Resolves `true` if playback was started via the
 * cloud audio, `false` if the Worker was unavailable (caller should fall back).
 * `speaker`/`engine` are forwarded only when present (back-compat with the
 * Worker, which defaults to its upgraded speaker).
 */
async function speakViaWorker(
  text: string,
  lang: string,
  rate: number,
  speaker?: string,
  engine?: 'melotts',
): Promise<boolean> {
  try {
    const res = await fetch(`${WORKER_URL}/tts`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        text,
        lang,
        ...(speaker ? { speaker } : {}),
        ...(engine ? { engine } : {}),
      }),
    });
    if (!res.ok) return false; // Non-2xx (incl. 501 not-configured).
    const bytes = await res.arrayBuffer();
    await playAudioBytes(bytes, rate);
    return true;
  } catch {
    return false; // Network error / fetch threw.
  }
}

/** Stop the current playback (both Audio and speechSynthesis paths). */
export function stopSpeaking(): void {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio = null;
  }
  const synth = (globalThis as any).speechSynthesis as
    | SpeechSynthesis
    | undefined;
  if (synth) synth.cancel();
}

/** Play raw audio bytes via an HTMLAudioElement, resolving when done. */
function playAudioBytes(bytes: ArrayBuffer, rate: number): Promise<void> {
  return new Promise<void>(resolve => {
    const blob = new Blob([bytes], { type: 'audio/mpeg' });
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.playbackRate = rate;
    currentAudio = audio;

    const finish = (): void => {
      URL.revokeObjectURL(url);
      if (currentAudio === audio) currentAudio = null;
      resolve();
    };

    audio.addEventListener('ended', finish, { once: true });
    audio.addEventListener('error', finish, { once: true });

    const playResult = audio.play() as unknown as Promise<void> | undefined;
    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => finish());
    }
  });
}

/**
 * Web Speech API path (en-US voice + rate). Resolves `true` once the device
 * voice finished speaking, or `false` immediately if no device voice exists
 * (so the caller can fall back to the cloud Worker).
 */
function speakViaSpeechSynthesis(
  text: string,
  lang: string,
  rate: number,
): Promise<boolean> {
  return new Promise<boolean>(resolve => {
    const synth = (globalThis as any).speechSynthesis as
      | SpeechSynthesis
      | undefined;
    const Utterance = (globalThis as any).SpeechSynthesisUtterance as
      | typeof SpeechSynthesisUtterance
      | undefined;
    if (!synth || !Utterance) {
      // No device TTS — signal the caller to fall back.
      resolve(false);
      return;
    }

    const utt = new Utterance(text);
    utt.lang = lang === 'en' ? 'en-US' : lang;
    utt.rate = rate;
    utt.addEventListener('end', () => resolve(true), { once: true });
    utt.addEventListener('error', () => resolve(true), { once: true });
    synth.speak(utt);
  });
}
